import { FormEvent, useEffect, useState } from "react";
import { CheckCheck, Clock3, Fuel, Gauge, ShieldCheck, Snowflake, Thermometer, X } from "lucide-react";
import {
  approveReeferCommand,
  listReeferCommands,
  listReefers,
  listTrailers,
  requestReeferSetpoint,
} from "../api";
import type { AuthUser, ReeferCommand, ReeferUnit, Trailer } from "../types";

export function ReeferPanel({
  open,
  onClose,
  user,
}: {
  open: boolean;
  onClose: () => void;
  user: AuthUser;
}) {
  const [units, setUnits] = useState<ReeferUnit[]>([]);
  const [trailers, setTrailers] = useState<Trailer[]>([]);
  const [commands, setCommands] = useState<ReeferCommand[]>([]);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [setpoint, setSetpoint] = useState("4");
  const [reason, setReason] = useState("");
  const [error, setError] = useState("");
  const canControl = ["superadmin", "fleet_admin", "maintenance"].includes(user.role);

  function refresh() {
    setError("");
    Promise.all([listReefers(), listReeferCommands(), listTrailers()])
      .then(([rows, commandRows, trailerRows]) => {
        setUnits(rows);
        setCommands(commandRows);
        setTrailers(trailerRows);
        setSelectedId((current) => current ?? rows[0]?.id ?? null);
      })
      .catch((cause) => setError(cause instanceof Error ? cause.message : "No fue posible cargar reefers"));
  }

  useEffect(() => {
    if (!open) return;
    refresh();
    window.addEventListener("gsf:reefer-updated", refresh);
    return () => window.removeEventListener("gsf:reefer-updated", refresh);
  }, [open]);

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!selectedId) return;
    try {
      await requestReeferSetpoint(selectedId, Number(setpoint), reason);
      setReason("");
      refresh();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "No fue posible solicitar el cambio");
    }
  }

  async function approve(commandId: number) {
    try {
      await approveReeferCommand(commandId);
      refresh();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "No fue posible aprobar");
    }
  }

  if (!open) return null;
  return (
    <div className="admin-overlay" role="presentation" onMouseDown={onClose}>
      <aside className="admin-panel reefer-panel" role="dialog" aria-modal="true" onMouseDown={(event) => event.stopPropagation()}>
        <header>
          <div>
            <span className="eyebrow">REEFER OEM · DOBLE CONTROL</span>
            <h2>Cadena fría y comandos</h2>
          </div>
          <button onClick={onClose} aria-label="Cerrar cadena fría"><X size={18} /></button>
        </header>
        <section className="admin-panel__summary">
          <span><Snowflake size={17} /><b>{units.length}</b> unidades</span>
          <span><Gauge size={17} /><b>{units.filter((unit) => unit.status === "running").length}</b> operando</span>
          <span><Clock3 size={17} /><b>{commands.filter((command) => command.status === "pending_approval").length}</b> por aprobar</span>
        </section>
        <div className="reefer-panel__body">
          <section className="reefer-units">
            {units.map((unit) => {
              const trailer = trailers.find((item) => item.id === unit.trailer_id);
              return (
                <button className={selectedId === unit.id ? "is-selected" : ""} onClick={() => setSelectedId(unit.id)} key={unit.id}>
                  <header><Snowflake size={15} /><strong>{trailer?.unit_code || unit.serial_number}</strong><b>{unit.status}</b></header>
                  <div>
                    <span><small>SETPOINT</small><strong>{unit.setpoint_c}°C</strong></span>
                    <span><small>SUMINISTRO</small><strong>{unit.supply_temperature_c}°C</strong></span>
                    <span><small>RETORNO</small><strong>{unit.return_temperature_c}°C</strong></span>
                  </div>
                  <footer><span><Fuel size={10} /> {unit.fuel_percent}%</span><span>{Math.round(unit.engine_hours)} h</span></footer>
                </button>
              );
            })}
          </section>
          <section className="reefer-control">
            <div className="reefer-command-list">
              <span className="eyebrow">COLA DE AUTORIZACIÓN</span>
              {commands.map((command) => (
                <article key={command.id}>
                  <ShieldCheck size={16} />
                  <span><strong>{command.command_type}</strong><small>{command.reason}</small></span>
                  <b>{command.status}</b>
                  {command.status === "pending_approval" && canControl && (
                    <button
                      disabled={command.requested_by === user.id}
                      onClick={() => approve(command.id)}
                      title={command.requested_by === user.id ? "Debe aprobar otro usuario" : "Aprobar comando"}
                    >
                      <CheckCheck size={12} /> APROBAR
                    </button>
                  )}
                </article>
              ))}
            </div>
            <form onSubmit={submit}>
              <span className="eyebrow">SOLICITAR SETPOINT</span>
              <label>REEFER<select value={selectedId || ""} onChange={(event) => setSelectedId(Number(event.target.value))}>{units.map((unit) => <option value={unit.id} key={unit.id}>{unit.serial_number}</option>)}</select></label>
              <label>TEMPERATURA °C<input type="number" min="-30" max="30" step=".5" value={setpoint} onChange={(event) => setSetpoint(event.target.value)} /></label>
              <label className="reefer-reason">MOTIVO<input minLength={10} required value={reason} onChange={(event) => setReason(event.target.value)} placeholder="Motivo operativo obligatorio" /></label>
              <button className="primary-button" disabled={!canControl || !selectedId}><Thermometer size={14} /> SOLICITAR CAMBIO</button>
              <small>Requiere aprobación de otro usuario. El despacho OEM permanece deshabilitado.</small>
            </form>
          </section>
        </div>
        {error && <div className="admin-message admin-message--error">{error}</div>}
      </aside>
    </div>
  );
}
